import { toCanvas } from 'html-to-image';

const CAPTURE_PIXEL_RATIO = 2;
const CAPTURE_OUTER_PADDING = 16;
const CAPTURE_BACKGROUND_COLOR = '#ffffff';
export const CAPTURE_MAX_CANVAS_EDGE = 16384;
export const CAPTURE_MAX_CANVAS_PIXELS = 16 * 1024 * 1024;

export class ChartCaptureSizeError extends Error {
    constructor(message = '画像サイズがブラウザの安全上限を超えています。') {
        super(message);
        this.name = 'ChartCaptureSizeError';
    }
}

const assertSafeCanvasSize = (width: number, height: number): void => {
    if (
        width > CAPTURE_MAX_CANVAS_EDGE ||
        height > CAPTURE_MAX_CANVAS_EDGE ||
        width * height > CAPTURE_MAX_CANVAS_PIXELS
    ) {
        throw new ChartCaptureSizeError();
    }
};

const waitForFonts = async (): Promise<void> => {
    if (typeof document === 'undefined' || !document.fonts?.ready) {
        return;
    }

    await document.fonts.ready;
};

const createPaddedCanvas = (
    sourceCanvas: HTMLCanvasElement,
    sourceWidth: number,
    sourceHeight: number,
): HTMLCanvasElement => {
    if (sourceCanvas.width <= 0 || sourceCanvas.height <= 0) {
        throw new Error('有効な画像用キャンバスを生成できませんでした');
    }

    const paddingX = Math.max(
        1,
        Math.round(CAPTURE_OUTER_PADDING * (sourceCanvas.width / sourceWidth)),
    );
    const paddingY = Math.max(
        1,
        Math.round(CAPTURE_OUTER_PADDING * (sourceCanvas.height / sourceHeight)),
    );
    const paddedWidth = sourceCanvas.width + paddingX * 2;
    const paddedHeight = sourceCanvas.height + paddingY * 2;
    assertSafeCanvasSize(paddedWidth, paddedHeight);
    const paddedCanvas = document.createElement('canvas');
    paddedCanvas.width = paddedWidth;
    paddedCanvas.height = paddedHeight;

    const context = paddedCanvas.getContext('2d');
    if (!context) {
        throw new Error('画像用キャンバスを初期化できませんでした');
    }

    context.fillStyle = CAPTURE_BACKGROUND_COLOR;
    context.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
    context.drawImage(sourceCanvas, paddingX, paddingY);

    return paddedCanvas;
};

const canvasToPngBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
    new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

export const domToImageBlob = async (element: HTMLElement): Promise<Blob> => {
    await waitForFonts();

    const rect = element.getBoundingClientRect();
    const width = Math.ceil(rect.width || element.offsetWidth);
    const height = Math.ceil(rect.height || element.offsetHeight);

    if (width <= 0 || height <= 0) {
        throw new Error('画像化対象のサイズを取得できませんでした');
    }

    const estimatedCanvasWidth =
        Math.ceil(width * CAPTURE_PIXEL_RATIO) +
        CAPTURE_OUTER_PADDING * CAPTURE_PIXEL_RATIO * 2;
    const estimatedCanvasHeight =
        Math.ceil(height * CAPTURE_PIXEL_RATIO) +
        CAPTURE_OUTER_PADDING * CAPTURE_PIXEL_RATIO * 2;
    assertSafeCanvasSize(estimatedCanvasWidth, estimatedCanvasHeight);

    const options = {
        backgroundColor: CAPTURE_BACKGROUND_COLOR,
        pixelRatio: CAPTURE_PIXEL_RATIO,
        cacheBust: true,
        width,
        height,
    };

    try {
        const sourceCanvas = await toCanvas(element, options);
        const paddedCanvas = createPaddedCanvas(sourceCanvas, width, height);
        const blob = await canvasToPngBlob(paddedCanvas);

        if (!blob || blob.size === 0 || blob.type !== 'image/png') {
            throw new Error('有効なPNG画像を生成できませんでした');
        }

        return blob;
    } catch (error) {
        console.error('画像の生成に失敗しました', error);
        throw error;
    }
};
